<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { usePendingTermsStore } from "@/stores/pendingTermsStore";
import { useAcceptTermsDocumentVersion } from "@/composables/models/terms/useTerms";
import { useStopImpersonatingSystemPracticeStaff } from "@/composables/models/staff/useSystemPracticeStaff";
import { useAuthStore } from "@/stores/authStore";
import { formatName } from "@/utils/Formatters";
import CheckboxField from "@/components/ui/inputs/CheckboxField.vue";
import BaseDialog from "@/components/shared/dialogs/BaseDialog.vue";
import BaseDialogActions from "@/components/shared/dialogs/BaseDialogActions.vue";
import ConfirmDialog from "@/components/shared/dialogs/ConfirmDialog.vue";
import PendingTermRow from "@/components/shared/terms/PendingTermRow.vue";

const { t } = useI18n();
const pendingTermsStore = usePendingTermsStore();
const { pendingTerms, isBlocked } = storeToRefs(pendingTermsStore);

const authStore = useAuthStore();
const { isImpersonating, loggedAsStaff } = storeToRefs(authStore);
const { mutateAsync: stopImpersonating } = useStopImpersonatingSystemPracticeStaff();

const { mutateAsync } = useAcceptTermsDocumentVersion();

const accepted = ref(false);
const loading = ref(false);

/**
 * Per-document revision state, reported by each row as its own version query resolves. The rows own
 * the fetching; this only aggregates, because the dialog title ("Updated Policy Documents" vs plain
 * "Policy Documents") is a property of the whole set and no single row can decide it.
 */
const revisionByDocId = ref<Record<string, boolean>>({});

const setRevision = (docId: string, isRevision: boolean) => {
  revisionByDocId.value[docId] = isRevision;
};

watch(pendingTerms, () => {
  // Fresh consent, and fresh revision state, for every new set of pending documents.
  accepted.value = false;
  revisionByDocId.value = {};
});

/** Nothing reads as "updated" unless this user actually signed an earlier version of something. */
const hasAnyRevision = computed(() =>
  pendingTerms.value.some((term) => revisionByDocId.value[term.id])
);

const dialogTitle = computed(() =>
  hasAnyRevision.value
    ? t("generic.dialog.pendingTerms.title")
    : // Plural form of the shared noun — the dialog always lists at least one document, and the
      // count line below it carries the exact number.
      t("generic.entity.policyDocument", 2)
);

const dialogSubtitle = computed(() =>
  hasAnyRevision.value
    ? t("generic.dialog.pendingTerms.subtitle")
    : t("generic.dialog.pendingTerms.firstTimeSubtitle")
);

const termsToAccept = computed(() => pendingTerms.value.filter((term) => term.version != null));

/**
 * The store is the only thing allowed to close this dialog. `BaseDialog` uses `defineModel`, so
 * without an `update:model-value` listener any internal write (Vuetify's own close paths, the
 * back-guard's synthetic Escape) would flip its *local* copy and dismiss the dialog for good while
 * `isBlocked` stayed true — leaving the user in an app they never accepted the terms for.
 * Swallowing the event keeps `:model-value="isBlocked"` authoritative: it can only close via
 * `clearBlock()` after a successful accept.
 */
const onDialogModelUpdate = () => {
  // Intentionally empty — see above.
};

/**
 * There is no reject endpoint — declining just ends the session that is being blocked. While
 * impersonating that means dropping back to the master account rather than signing out, because the
 * pending documents belong to the impersonated staff member and only they can accept them; logging
 * the impersonator out would punish the wrong account.
 */
const isDeclineConfirmOpen = ref(false);
const declining = ref(false);

const declineCopy = computed(() =>
  isImpersonating.value
    ? "generic.dialog.pendingTerms.declineImpersonatingConfirm"
    : "generic.dialog.pendingTerms.declineConfirm"
);

/** Depicts what actually happens — leaving the session — rather than a deletion. */
const declineIcon = computed(() => (isImpersonating.value ? "mdi-incognito-off" : "mdi-logout"));

/**
 * Names the impersonated staff member, because an impersonator *can* accept on their behalf — the
 * signature is just recorded against them. Saying who that is makes the consequence of the other
 * button concrete instead of implying the choice is unavailable.
 */
const declineSubtitle = computed(() => {
  // `formatName` renders "undefined undefined" for a missing record; fall back to a generic noun
  // rather than putting that in the middle of a consent sentence.
  const staff = loggedAsStaff.value;
  const name =
    staff?.first_name || staff?.last_name
      ? formatName(staff)
      : t("generic.dialog.pendingTerms.declineImpersonatingConfirm.fallbackName");

  return t(`${declineCopy.value}.subtitle`, { name });
});

const handleDecline = async () => {
  declining.value = true;

  try {
    if (isImpersonating.value) {
      // Keep the block up across this one: `stopImpersonating` reloads the page, and its own
      // `onSuccess` clears the query cache. Clearing the block first would let every gated query
      // refetch against a session that is about to be replaced.
      await stopImpersonating();
      pendingTermsStore.clearBlock();
      return;
    }

    // Clears the block and the query cache itself — deliberately not done here first, because
    // releasing the block while a valid token is still present re-enables every query
    // `useQueryEnabled` was gating and stampedes the API with refetches that then 401.
    await authStore.userLogout();
  } finally {
    declining.value = false;
    isDeclineConfirmOpen.value = false;
  }
};

const handleAccept = async () => {
  loading.value = true;

  try {
    await Promise.all(
      termsToAccept.value.map((term) =>
        mutateAsync(
          { docId: term.id, version: term.version },
          { throwOnError: true, allowConcurrent: true }
        )
      )
    );

    pendingTermsStore.clearBlock();
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <BaseDialog
    data-testid="pending-terms-dialog"
    persistent
    :model-value="isBlocked"
    icon="mdi-file-document-outline"
    :show-close="false"
    @update:model-value="onDialogModelUpdate"
    blur-overlay
  >
    <template #title>
      {{ dialogTitle }}
    </template>
    <template #subtitle>
      {{ dialogSubtitle }}
    </template>
    <template #body>
      <p
        class="tw:mb-2.5 tw:flex tw:items-center tw:gap-2 tw:text-xs tw:font-semibold tw:tracking-wider tw:text-on-surface/50 tw:uppercase"
      >
        {{ t("generic.dialog.pendingTerms.documentCount", pendingTerms.length) }}
        <span class="tw:h-px tw:flex-1 tw:bg-on-surface/10" />
      </p>

      <div class="tw:flex tw:flex-col tw:gap-2">
        <pending-term-row
          v-for="term in pendingTerms"
          :key="term.id"
          :term="term"
          @update:revision="setRevision"
        />
      </div>

      <div
        class="tw:mt-3.5 tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:px-3 tw:py-3 tw:transition-colors tw:select-none"
        :class="
          accepted
            ? 'tw:border-primary/40 tw:bg-primary/5'
            : 'tw:border-on-surface/10 tw:bg-on-surface/[0.04]'
        "
        data-testid="pending-terms-checkbox"
        @click="accepted = !accepted"
      >
        <CheckboxField
          :model-value="accepted"
          @update:model-value="(v) => (accepted = v)"
          @click.stop
        />
        <span class="tw:text-md tw:leading-snug">
          {{ t("generic.dialog.pendingTerms.consentLabel") }}
        </span>
      </div>
    </template>
    <template #actions>
      <BaseDialogActions
        :secondary="{
          icon: 'mdi-cancel',
          action: () => {
            isDeclineConfirmOpen = true;
          },
          text: t('generic.action.decline'),
          tone: 'danger',
          disabled: loading,
        }"
        :primary="{
          icon: 'mdi-arrow-right',
          disabled: !accepted,
          loading: loading,
          action: handleAccept,
          text: t('generic.action.continue'),
        }"
        align="between"
      />
    </template>
  </BaseDialog>

  <confirm-dialog
    v-model="isDeclineConfirmOpen"
    :title="t(`${declineCopy}.title`)"
    :subtitle="declineSubtitle"
    :accept-text="t(`${declineCopy}.confirmButton`)"
    :accept-action="handleDecline"
    :loading="declining"
    :icon="declineIcon"
    delete
    data-testid="pending-terms-decline-confirm"
  />
</template>
